import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useHistory from "routing/useHistory";
import { Button, FormGroup, Input, Modal } from "components/bootstrap";
import { Spinner, Select, ModalButtonToolbar } from "components/common";
import Routes from "routing/Routes";
import useEventDefinitionMutations from "components/event-definitions/hooks/useEventDefinitionMutations";
import useLicenseEventDefinitionTemplates from "license/hooks/useLicenseEventDefinitionTemplates";
import NumberUtils from "util/NumberUtils";
import { THRESHOLD_MODES } from "./types";
const Description = styled.p`
  margin-bottom: ${({ theme }) => theme.spacings.md};
`;
const THRESHOLD_LABELS = {
  PERCENTAGE: "license.traffic.modal.threshold_percentage_label",
  ABSOLUTE: "license.traffic.modal.threshold_absolute_label"
};
const THRESHOLD_OPTIONS = {
  PERCENTAGE: "license.traffic.modal.option_percentage",
  ABSOLUTE: "license.traffic.modal.option_absolute"
};
const THRESHOLD_HELP = {
  PERCENTAGE: "license.traffic.modal.help_percentage",
  ABSOLUTE: "license.traffic.modal.help_absolute"
};
const EVENT_TITLE_TEMPLATES = {
  PERCENTAGE: (threshold) => `License usage \u2265 ${threshold}%`,
  ABSOLUTE: (formattedThreshold) => `License usage \u2265 ${formattedThreshold}`
};
const TRAFFIC_EVENT_DEFINITION_TYPE = "traffic-v1";
const SUPPORTED_THRESHOLD_MODES = [THRESHOLD_MODES.ABSOLUTE, THRESHOLD_MODES.PERCENTAGE];
const validateThreshold = (template, threshold) => {
  if (!template || threshold === void 0 || threshold === null) return false;
  if (template.config.threshold_mode === THRESHOLD_MODES.PERCENTAGE) {
    return threshold > 0 && threshold <= 100;
  }
  return threshold > 0;
};
const generateEventTitle = (template, customValue) => {
  const threshold = customValue || template.config.threshold;
  if (template.config.threshold_mode === THRESHOLD_MODES.PERCENTAGE) {
    return EVENT_TITLE_TEMPLATES.PERCENTAGE(threshold);
  }
  return EVENT_TITLE_TEMPLATES.ABSOLUTE(NumberUtils.formatBytes(threshold));
};
const LicenseTrafficEventDefinitionModal = () => {
  const { t } = useTranslation("enterprise_license");
  const [showModal, setShowModal] = useState(false);
  const [selectedThresholdMode, setSelectedThresholdMode] = useState(null);
  const [customThreshold, setCustomThreshold] = useState(void 0);
  const [isCreating, setIsCreating] = useState(false);
  const { push } = useHistory();
  const { createEventDefinition } = useEventDefinitionMutations();
  const { data, isLoading } = useLicenseEventDefinitionTemplates({
    enabled: showModal
  });
  const templates = data != null ? data : [];
  const supportedTemplates = templates.filter(
    (template) => template.config.type === TRAFFIC_EVENT_DEFINITION_TYPE && SUPPORTED_THRESHOLD_MODES.includes(template.config.threshold_mode)
  );
  const templateOptions = supportedTemplates.map((template) => ({
    value: template.config.threshold_mode,
    label: t(THRESHOLD_OPTIONS[template.config.threshold_mode])
  }));
  const selectedTemplate = templates.find((template) => template.config.threshold_mode === selectedThresholdMode);
  const handleOpen = () => {
    setShowModal(true);
  };
  const handleClose = () => {
    setShowModal(false);
    setSelectedThresholdMode(null);
    setCustomThreshold(void 0);
  };
  const handleThresholdModeChange = (thresholdMode) => {
    setSelectedThresholdMode(thresholdMode);
    const template = templates.find((candidate) => candidate.config.threshold_mode === thresholdMode);
    if (template) {
      if (thresholdMode === THRESHOLD_MODES.ABSOLUTE) {
        setCustomThreshold(NumberUtils.bytesToGiB(template.config.threshold));
      } else {
        setCustomThreshold(template.config.threshold);
      }
    }
  };
  const isValidThreshold = validateThreshold(selectedTemplate, customThreshold);
  const isAbsoluteMode = (selectedTemplate == null ? void 0 : selectedTemplate.config.threshold_mode) === THRESHOLD_MODES.ABSOLUTE;
  const handleCreate = async () => {
    if (!selectedTemplate || !isValidThreshold || customThreshold === void 0) return;
    const thresholdInBytes = isAbsoluteMode ? NumberUtils.giBToBytes(customThreshold) : customThreshold;
    const eventDefinition = {
      ...selectedTemplate,
      title: generateEventTitle(selectedTemplate, thresholdInBytes),
      config: {
        ...selectedTemplate.config,
        threshold: thresholdInBytes
      }
    };
    setIsCreating(true);
    createEventDefinition(eventDefinition).then((createdDefinition) => {
      handleClose();
      if (createdDefinition == null ? void 0 : createdDefinition.id) {
        push(Routes.ALERTS.DEFINITIONS.show(createdDefinition.id));
      }
    }).finally(() => {
      setIsCreating(false);
    });
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Button, { bsStyle: "success", onClick: handleOpen }, t("license.traffic.modal.open_button")), /* @__PURE__ */ React.createElement(Modal, { show: showModal, onHide: handleClose }, /* @__PURE__ */ React.createElement(Modal.Header, { showCloseButton: true }, /* @__PURE__ */ React.createElement(Modal.Title, null, t("license.traffic.modal.title"))), /* @__PURE__ */ React.createElement(Modal.Body, null, isLoading && /* @__PURE__ */ React.createElement(Spinner, { text: t("license.traffic.modal.loading_templates") }), !isLoading && supportedTemplates.length === 0 && /* @__PURE__ */ React.createElement(Description, null, t("license.traffic.modal.no_templates")), !isLoading && supportedTemplates.length > 0 && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Description, null, t("license.traffic.modal.intro")), /* @__PURE__ */ React.createElement(Description, null, t("license.traffic.modal.notification_note")), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement("label", { htmlFor: "threshold-type-select" }, t("license.traffic.modal.threshold_type_label")), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "threshold-type-select",
      placeholder: t("license.traffic.modal.select_placeholder"),
      value: selectedThresholdMode,
      onChange: handleThresholdModeChange,
      options: templateOptions,
      required: true
    }
  )), selectedTemplate && /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "custom-threshold",
      type: "number",
      step: selectedTemplate.config.threshold_mode === THRESHOLD_MODES.ABSOLUTE ? 0.01 : void 0,
      value: customThreshold,
      onChange: (e) => setCustomThreshold(Number(e.target.value)),
      label: t(THRESHOLD_LABELS[selectedTemplate.config.threshold_mode]),
      help: t(THRESHOLD_HELP[selectedTemplate.config.threshold_mode]),
      bsStyle: isValidThreshold ? void 0 : "error",
      required: true
    }
  ))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(ModalButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { onClick: handleClose, disabled: isCreating }, t("license.traffic.modal.cancel")), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      onClick: handleCreate,
      disabled: !selectedTemplate || !isValidThreshold || isCreating
    },
    isCreating ? t("license.traffic.modal.creating") : t("license.traffic.modal.create")
  )))));
};
export default LicenseTrafficEventDefinitionModal;
